import { FormEvent, PropsWithChildren, ReactNode } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { kebabCase } from 'lodash-es';

import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  chakra,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  Radio,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { LanguageIcon } from '@udacity/chakra-uds-icons';

import { SelectInput } from '~/components/select-input';
import { trackEvent } from '~/features/analytics/track-event';
import { getSkillFilterOptionsWithLocaleLabels } from '~/features/catalog/catalog-filter-data';
import { useCatalogFilterLanguageQueryParam } from '~/features/catalog/hooks/use-catalog-filter-language-query-param';
import { useCatalogFilterLocaleMetadata } from '~/features/catalog/hooks/use-catalog-filter-locale-metadata';
import { useCatalogFilterOptions } from '~/features/catalog/hooks/use-catalog-filter-options';
import { Filters, Language, Scope, UriFilter } from '~/features/catalog/models/filter';
import { clearAllFilters, updateCatalogUrlState } from '~/features/catalog/utils/catalog-url-state-utils';
import { getActiveSubscriptionCookie } from '~/features/cookies/utils/get-active-subscription-cookie';
import { useUser } from '~/features/user/hooks/use-user';
import { useClientRender } from '~/hooks/use-client-render';
import { useI18n } from '~/hooks/use-i18n';
import { isSelectOption } from '~/types/select-option';

import {
  AcademicDegreeTypeIcon,
  BookIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CourseTypeIcon,
  Difficulty3Icon,
  FilterIcon,
  GearIcon,
  GraduationCapIcon,
  MoneyIcon,
  NanodegreeTypeIcon,
  SchoolIcon,
  StopWatchIcon,
} from '../icons';

import { CatalogSort } from './catalog-sort';

// Reusable checkbox component for filter options
function FilteredCheckbox({
  isChecked,
  name,
  value,
  onChange,
  iconSlot,
  children,
}: {
  isChecked: boolean;
  name: string;
  value: string;
  onChange: (event: FormEvent<HTMLInputElement>) => void;
  iconSlot?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Checkbox
      bgColor='grey.50'
      isChecked={isChecked}
      name={name}
      padding='16px 18px'
      size={{ base: 'lg', lg: 'md' }}
      value={value}
      _first={{
        borderTop: '1px solid',
        borderTopColor: 'grey.200',
      }}
      _notLast={{
        borderBottom: '1px solid',
        borderBottomColor: 'grey.200',
      }}
      onChange={onChange}
    >
      <Flex alignItems='center' color='grey.500' display='inline-flex' fontSize='0.8125rem' gap='10px' lineHeight='1'>
        {iconSlot && (
          <Box as='span' flexShrink={0}>
            {iconSlot}
          </Box>
        )}
        {children}
      </Flex>
    </Checkbox>
  );
}

export function CatalogFilters({
  selectedFilters,
  numberOfSelectedFilters,
  numberOfResults = 0,
  skillUriFilters,
  isLoading,
}: {
  selectedFilters: Filters;
  numberOfSelectedFilters: number;
  numberOfResults?: number;
  skillUriFilters: UriFilter[];
  isLoading?: boolean;
}) {
  const router = useRouter();
  const { t } = useI18n();
  const { isOpen: isFilterDrawOpen, onOpen: openFilterDrawer, onClose: closeFilterDrawer } = useDisclosure();

  function handleFiltersClearClick() {
    clearAllFilters(router);
  }

  return (
    <>
      <Flex display={{ base: 'none', lg: 'flex' }} flexDirection='column' gap='8px'>
        <FilterForm selectedFilters={selectedFilters} skillUriFilters={skillUriFilters} />
      </Flex>
      <Box display={{ base: 'block', lg: 'none' }}>
        <Button
          leftIcon={<FilterIcon height='16px' width='16px' />}
          size='md'
          variant='outline'
          onClick={openFilterDrawer}
        >
          {t('catalog.filters.openButton')} {Boolean(numberOfSelectedFilters) && `(${numberOfSelectedFilters})`}
        </Button>
      </Box>
      <Drawer isOpen={isFilterDrawOpen} placement='right' size='xl' variant='mobile' onClose={closeFilterDrawer}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader
            alignItems='center'
            boxShadow='none'
            display='flex'
            justifyContent='space-between'
            pe='8px'
            ps='16px'
          >
            <Heading as='p' color='grey.900' flex={1} fontSize='1.125rem' lineHeight='1.4' size='label'>
              {t('catalog.filters.drawerTitle')}
            </Heading>
            <DrawerCloseButton />
          </DrawerHeader>
          <DrawerBody padding='16px'>
            <FilterForm selectedFilters={selectedFilters} skillUriFilters={skillUriFilters} isMobile />
          </DrawerBody>
          <DrawerFooter
            backgroundColor='white'
            borderTop='1px solid'
            borderTopColor='grey.200'
            boxShadow='none'
            gap='16px'
          >
            <Box flex={1} flexShrink={0} width='100%'>
              <Button variant='outline' width='100%' onClick={handleFiltersClearClick}>
                {t('catalog.filters.clear', { count: numberOfSelectedFilters })}
              </Button>
            </Box>
            <Box flex={1} flexShrink={0} width='100%'>
              <Button isLoading={isLoading} width='100%' onClick={closeFilterDrawer}>
                {t('catalog.filters.results', { count: numberOfResults })}
              </Button>
            </Box>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}

export function FilterForm({
  selectedFilters,
  skillUriFilters,
  isMobile,
}: {
  selectedFilters: Filters;
  skillUriFilters: UriFilter[];
  isMobile?: boolean;
}) {
  const router = useRouter();
  const { t, isTranslated } = useI18n();
  const { userId } = useUser();
  const userHasAllAccess = getActiveSubscriptionCookie();
  const clientRender = useClientRender();
  const isBrowsingEnterprise = Boolean(router.query.companySlug);

  // Handle language change to clear the language filter when switching languages
  useCatalogFilterLanguageQueryParam();

  function handleSelectedFiltersChange(filterType: keyof Filters, checked: boolean, value: string) {
    let newSelectedFilters = [...(selectedFilters[filterType] ?? [])];

    if (checked) {
      newSelectedFilters.push(value);
      trackEvent({
        type: 'click',
        name: 'catalogSearched',
        action: 'catalog_filter_clicked',
        label: filterType,
        value: newSelectedFilters.join(', '),
      });
    } else {
      const removedPriceIndex = newSelectedFilters.indexOf(value);
      newSelectedFilters.splice(removedPriceIndex, 1);
    }

    updateCatalogUrlState({
      router,
      updatedRouteParameters: {
        page: undefined,
        [filterType]: newSelectedFilters.join(',') || undefined,
      },
    });
  }

  function handleRadioFilterChange(filterType: keyof Filters, value: string) {
    trackEvent({
      type: 'click',
      name: 'catalogSearched',
      action: 'catalog_filter_clicked',
      label: filterType,
      value,
    });
    updateCatalogUrlState({
      router,
      updatedRouteParameters: {
        page: undefined,
        [filterType]: filterType === 'language' && value === 'all' ? undefined : value,
      },
    });
  }

  const MAX_ACCORDION_ITEMS = 9;

  return (
    <chakra.form>
      <Flex
        flexDirection='column'
        paddingY={{
          md: '24px',
        }}
      >
        {isMobile && (
          <Accordion defaultIndex={isMobile ? 0 : undefined} mb={4} allowToggle>
            <FilterGroup
              iconSlot={<FilterIcon height='16px' width='16px' />}
              numberOfSelectedFilters={0}
              title={t('catalog.filters.group.sort') ?? ''}
            >
              <CatalogSort />
            </FilterGroup>
          </Accordion>
        )}

        <Accordion
          defaultIndex={isMobile ? Array.from({ length: MAX_ACCORDION_ITEMS }, (_, i) => i) : undefined}
          allowMultiple
          allowToggle
        >
          {isTranslated && (
            <FilterGroup
              iconSlot={<LanguageIcon height='16px' width='16px' />}
              title={t('catalog.filters.language.title') ?? ''}
            >
              <LanguageFilter
                selectedLanguage={selectedFilters.language?.[0]}
                onFiltersChange={handleRadioFilterChange}
              />
              <Text fontSize='xs' marginTop='16px'>
                {t('catalog.filters.language.note')}
              </Text>
            </FilterGroup>
          )}

          {userId && clientRender && (
            <FilterGroup
              iconSlot={<GraduationCapIcon height='16px' width='16px' />}
              title={t('catalog.filters.group.programs') ?? ''}
            >
              <ScopeFilter selectedScope={selectedFilters.scope?.[0]} onFiltersChange={handleRadioFilterChange} />
            </FilterGroup>
          )}

          {/* School Filter - First in the new order */}
          <FilterGroup
            iconSlot={<SchoolIcon height='16px' width='16px' />}
            numberOfSelectedFilters={selectedFilters.school?.length ?? 0}
            title={t('catalog.filters.group.school') ?? ''}
          >
            <SchoolFilter
              selectedSchools={selectedFilters.school ?? []}
              onFiltersChange={handleSelectedFiltersChange}
            />
          </FilterGroup>

          {/* Price Filter - Second in the new order */}
          {!userHasAllAccess && !isBrowsingEnterprise && clientRender && (
            <FilterGroup
              iconSlot={<MoneyIcon height='16px' width='16px' />}
              numberOfSelectedFilters={selectedFilters.price?.length ?? 0}
              title={t('catalog.filters.group.price') ?? ''}
            >
              <PriceFilter selectedPrices={selectedFilters.price ?? []} onFiltersChange={handleSelectedFiltersChange} />
            </FilterGroup>
          )}

          {/* instead of price, enterprise users can filter by company availability. This toggles the autoEnrollable param */}
          {isBrowsingEnterprise && (
            <>
              <FilterGroup
                numberOfSelectedFilters={selectedFilters.availability?.length ?? 0}
                title={t('catalog.filters.group.availability') ?? ''}
              >
                <AvailabilityFilter
                  selectedAvailability={selectedFilters.availability ?? []}
                  onFiltersChange={handleSelectedFiltersChange}
                />
              </FilterGroup>
            </>
          )}

          {/* Type Filter - Third in the new order */}
          <FilterGroup
            iconSlot={<BookIcon height='16px' width='16px' />}
            numberOfSelectedFilters={selectedFilters.type?.length ?? 0}
            title={t('catalog.filters.group.type') ?? ''}
          >
            <TypeFilter selectedTypes={selectedFilters.type ?? []} onFiltersChange={handleSelectedFiltersChange} />
          </FilterGroup>

          {/* Skill Filter - Fourth in the new order */}
          <FilterGroup
            iconSlot={<GearIcon height='16px' width='16px' />}
            numberOfSelectedFilters={selectedFilters.skill?.length ?? 0}
            title={t('catalog.filters.group.skill') ?? ''}
          >
            <SkillFilter
              isMobile={isMobile}
              selectedSkill={selectedFilters.skill ?? []}
              skillUriFilters={skillUriFilters}
            />
          </FilterGroup>

          {/* Level (Difficulty) Filter - Fifth in the new order */}
          <FilterGroup
            iconSlot={<Difficulty3Icon height='16px' width='16px' />}
            numberOfSelectedFilters={selectedFilters.difficulty?.length ?? 0}
            title={t('catalog.filters.group.difficulty') ?? ''}
          >
            <DifficultyLevelFilter
              selectedDifficulties={selectedFilters.difficulty ?? []}
              onFiltersChange={handleSelectedFiltersChange}
            />
          </FilterGroup>

          {/* Duration Filter - Sixth in the new order */}
          <FilterGroup
            iconSlot={<StopWatchIcon height='16px' width='16px' />}
            numberOfSelectedFilters={selectedFilters.duration?.length ?? selectedFilters.rawDuration?.length ?? 0}
            title={t('catalog.filters.group.duration') ?? ''}
          >
            <RawDurationFilter
              selectedDurations={selectedFilters.rawDuration ?? []}
              onFiltersChange={handleSelectedFiltersChange}
            />
          </FilterGroup>
        </Accordion>
      </Flex>
    </chakra.form>
  );
}

export function FilterGroup({
  title,
  iconSlot = null,
  numberOfSelectedFilters,
  children,
}: PropsWithChildren<{
  title?: string;
  iconSlot?: ReactNode;
  numberOfSelectedFilters?: number;
}>) {
  return (
    <AccordionItem
      isolation='isolate'
      _first={{
        backgroundColor: 'grey.50',
        '.chakra-accordion__button': {
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
        },
      }}
      _last={{
        backgroundColor: 'grey.50',
        '.chakra-accordion__button[aria-expanded="false"]': {
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
        },
        '.chakra-accordion__button[aria-expanded="true"] + .chakra-collapse > .chakra-accordion__panel': {
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
        },
      }}
    >
      {({ isExpanded }) => (
        <>
          <AccordionButton borderColor='grey.200' color='grey.500' p='16px'>
            <Flex
              alignItems='center'
              as='span'
              color={isExpanded ? 'blue.500' : 'inherit'}
              flex='1'
              fontSize='1rem'
              fontWeight='400'
              gap='10px'
              lineHeight='1.5'
              textAlign='left'
            >
              {iconSlot} {title} {Boolean(numberOfSelectedFilters) && `(${numberOfSelectedFilters})`}
            </Flex>
            {isExpanded ? (
              <ChevronUpIcon color='blue.500' height='16px' width='16px' />
            ) : (
              <ChevronDownIcon color='grey.300' height='16px' width='16px' />
            )}
          </AccordionButton>
          <AccordionPanel overflow='hidden' p={4} sx={{ div: { position: 'relative' } }}>
            {children}
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  );
}

export function PriceFilter({
  selectedPrices,
  onFiltersChange,
}: {
  selectedPrices: string[];
  onFiltersChange: (filterType: keyof Filters, checked: boolean, value: string) => void;
}) {
  const { price: priceOptions } = useCatalogFilterOptions();

  function handleFiltersChange(event: FormEvent<HTMLInputElement>) {
    const { checked, value } = event.currentTarget;
    onFiltersChange('price', checked, value);
  }

  return (
    <Stack gap={0} mx='-16px' my='-16px'>
      {priceOptions.map((option) => (
        <FilteredCheckbox
          key={option.value}
          isChecked={selectedPrices?.includes(option.value)}
          name={`price--${option.value}`}
          value={option.value}
          onChange={handleFiltersChange}
        >
          {option.label}
        </FilteredCheckbox>
      ))}
    </Stack>
  );
}

export function AvailabilityFilter({
  selectedAvailability,
  onFiltersChange,
}: {
  selectedAvailability: string[];
  onFiltersChange: (filterType: keyof Filters, checked: boolean, value: string) => void;
}) {
  const { availability: availabilityOptions } = useCatalogFilterOptions();

  function handleFiltersChange(event: FormEvent<HTMLInputElement>) {
    const { checked, value } = event.currentTarget;
    onFiltersChange('availability', checked, value);
  }

  return (
    <Stack gap={0} mx='-16px' my='-16px'>
      {availabilityOptions.map((option) => (
        <FilteredCheckbox
          key={option.value}
          isChecked={selectedAvailability?.includes(option.value)}
          name={`availability--${option.value}`}
          value={option.value}
          onChange={handleFiltersChange}
        >
          {option.label}
        </FilteredCheckbox>
      ))}
    </Stack>
  );
}

export function SchoolFilter({
  selectedSchools,
  onFiltersChange,
}: {
  selectedSchools: string[];
  onFiltersChange: (filterType: keyof Filters, checked: boolean, value: string) => void;
}) {
  const { school: schoolOptions } = useCatalogFilterOptions();

  function handleFiltersChange(event: FormEvent<HTMLInputElement>) {
    const { checked, value } = event.currentTarget;
    onFiltersChange('school', checked, value);
  }

  function getSchoolIcon(schoolTitle: string) {
    const schoolIconMap = {
      'artificial-intelligence': '/images/school-icons/shields/artificial-intelligence.svg',
      'data-science': '/images/school-icons/shields/data-science.svg',
      business: '/images/school-icons/shields/business.svg',
      'executive-leadership': '/images/school-icons/shields/executive-leadership.svg',
      'programming-and-development': '/images/school-icons/shields/programming.svg',
      'cloud-computing': '/images/school-icons/shields/cloud-computing.svg',
      'autonomous-systems': '/images/school-icons/shields/autonomous-systems.svg',
      cybersecurity: '/images/school-icons/shields/cybersecurity.svg',
      'product-management': '/images/school-icons/shields/product-management.svg',
      'career-resources': '/images/school-icons/shields/career-resources.svg',
    };
    const schoolSlug = kebabCase(schoolTitle);
    return schoolIconMap[schoolSlug as keyof typeof schoolIconMap];
  }

  return (
    <Stack gap={0} mx='-16px' my='-16px'>
      {schoolOptions.map((school) => (
        <FilteredCheckbox
          key={school.value}
          isChecked={selectedSchools?.includes(school.value)}
          name={`school--${school.value}`}
          value={school.value}
          iconSlot={
            <Image alt='' height={20} loading='lazy' quality={50} src={getSchoolIcon(school.value)} width={18} />
          }
          onChange={handleFiltersChange}
        >
          {school.label}
        </FilteredCheckbox>
      ))}
    </Stack>
  );
}

export function SkillFilter({
  isMobile,
  selectedSkill,
  skillUriFilters,
}: {
  isMobile?: boolean;
  selectedSkill: string[];
  skillUriFilters: UriFilter[];
}) {
  const isClient = useClientRender();
  const router = useRouter();
  const { t, i18n } = useI18n();
  const { catalogFilterLocaleMetadata } = useCatalogFilterLocaleMetadata(i18n.language);
  const skillFilterOptions = getSkillFilterOptionsWithLocaleLabels(
    skillUriFilters,
    catalogFilterLocaleMetadata?.skills
  );
  const selectedSkillName = selectedSkill[0];
  const selectedSkillLabel = skillFilterOptions.find((option) => option.value === selectedSkillName)?.label;
  const isDesktopClient = isClient && !isMobile;

  function handleFiltersChange(selectedOption: unknown) {
    const skillValue = isSelectOption(selectedOption) ? selectedOption.value : undefined;
    if (skillValue) {
      trackEvent({
        type: 'click',
        name: 'catalogSearched',
        action: 'catalog_filter_clicked',
        label: 'skill',
        value: skillValue,
      });
    }
    updateCatalogUrlState({
      router,
      updatedRouteParameters: {
        page: undefined,
        skill: skillValue,
      },
    });
  }

  return (
    <SelectInput
      name='skill'
      options={skillFilterOptions}
      placeholder={t('catalog.filters.searchPlaceholder') ?? ''}
      selectProps={{
        isClearable: true,
        value: selectedSkill.length ? { label: selectedSkillLabel, value: selectedSkillName } : null,
        onChange: handleFiltersChange,
        menuPortalTarget: isDesktopClient ? document.body : undefined,
        ...(isDesktopClient && {
          menuPosition: 'fixed',
          menuPlacement: 'auto',
        }),
      }}
    />
  );
}

export function DifficultyLevelFilter({
  selectedDifficulties,
  onFiltersChange,
}: {
  selectedDifficulties: string[];
  onFiltersChange: (filterType: keyof Filters, checked: boolean, value: string) => void;
}) {
  const { difficulty: difficultyOptions } = useCatalogFilterOptions();

  function handleFiltersChange(event: FormEvent<HTMLInputElement>) {
    const { checked, value } = event.currentTarget;
    onFiltersChange('difficulty', checked, value);
  }

  return (
    <Stack gap={0} mx='-16px' my='-16px'>
      {difficultyOptions.map((option) => (
        <FilteredCheckbox
          key={option.value}
          isChecked={selectedDifficulties?.includes(option.value)}
          name={`difficulty--${option.value}`}
          value={option.value}
          onChange={handleFiltersChange}
        >
          {option.label}
        </FilteredCheckbox>
      ))}
    </Stack>
  );
}

export function RawDurationFilter({
  selectedDurations,
  onFiltersChange,
}: {
  selectedDurations: string[];
  onFiltersChange: (filterType: keyof Filters, checked: boolean, value: string) => void;
}) {
  const { rawDuration: rawDurationOptions } = useCatalogFilterOptions();

  function handleFiltersChange(event: FormEvent<HTMLInputElement>) {
    const { checked, value } = event.currentTarget;
    onFiltersChange('rawDuration', checked, value);
  }

  return (
    <Stack gap={0} mx='-16px' my='-16px'>
      {rawDurationOptions.map((option) => (
        <FilteredCheckbox
          key={option.value}
          isChecked={selectedDurations?.includes(option.value)}
          name={`rawDuration--${option.value}`}
          value={option.value}
          onChange={handleFiltersChange}
        >
          {option.label}
        </FilteredCheckbox>
      ))}
    </Stack>
  );
}

export function TypeFilter({
  selectedTypes,
  onFiltersChange,
}: {
  selectedTypes: string[];
  onFiltersChange: (filterType: keyof Filters, checked: boolean, value: string) => void;
}) {
  const { type: typeOptions } = useCatalogFilterOptions({ isMastersFilterEnabled: true });

  function handleFiltersChange(event: FormEvent<HTMLInputElement>) {
    const { checked, value } = event.currentTarget;
    onFiltersChange('type', checked, value);
  }

  function getTypeIcon(typeValue: string): ReactNode {
    switch (typeValue) {
      case 'course':
        return <CourseTypeIcon color='blue.500' height='16px' width='16px' />;
      case 'nanodegree program':
        return <NanodegreeTypeIcon color='blue.500' height='16px' width='16px' />;
      case 'masters':
        return <AcademicDegreeTypeIcon color='gold.300' height='16px' width='16px' />;
      default:
        return null;
    }
  }

  return (
    <Stack gap={0} mx='-16px' my='-16px'>
      {typeOptions.map((option) => (
        <FilteredCheckbox
          key={option.value}
          iconSlot={getTypeIcon(option.value)}
          isChecked={selectedTypes?.includes(option.value)}
          name={`type--${option.value}`}
          value={option.value}
          onChange={handleFiltersChange}
        >
          {option.label}
        </FilteredCheckbox>
      ))}
    </Stack>
  );
}

export function ScopeFilter({
  selectedScope = 'all',
  onFiltersChange,
}: {
  selectedScope?: Scope;
  onFiltersChange: (filterType: keyof Filters, value: string) => void;
}) {
  const { scope: scopeOptions } = useCatalogFilterOptions();

  function handleFiltersChange(event: FormEvent<HTMLInputElement>) {
    const { value } = event.currentTarget;
    onFiltersChange('scope', value);
  }

  return (
    <Stack>
      {scopeOptions.map((option) => (
        <Radio
          key={option.value}
          isChecked={selectedScope === option.value}
          name={`scope--${option.value}`}
          size={{ base: 'lg', lg: 'md' }}
          value={option.value}
          onChange={handleFiltersChange}
        >
          <Box as='span' color='grey.500' fontSize='0.8125rem' lineHeight='1.4'>
            {option.label}
          </Box>
        </Radio>
      ))}
    </Stack>
  );
}

export function LanguageFilter({
  selectedLanguage = 'all',
  onFiltersChange,
}: {
  selectedLanguage?: Language;
  onFiltersChange: (filterType: keyof Filters, value: string) => void;
}) {
  const { language: languageOptions } = useCatalogFilterOptions();

  function handleFiltersChange(event: FormEvent<HTMLInputElement>) {
    const { value } = event.currentTarget;
    onFiltersChange('language', value);
  }

  return (
    <Stack>
      {languageOptions.map((option) => (
        <Radio
          key={option.value}
          isChecked={selectedLanguage === option.value}
          name={`language--${option.value}`}
          size={{ base: 'lg', lg: 'md' }}
          value={option.value}
          onChange={handleFiltersChange}
        >
          <Box as='span' color='grey.500' fontSize='0.8125rem' lineHeight='1.4'>
            {option.label}
          </Box>
        </Radio>
      ))}
    </Stack>
  );
}
